// The page chrome around every Hub page: masthead, navigation, notice and
// footer, in the Paramount Liquor brand (PL Brand Guidelines v1.3), styled
// like the Promotions Uploader. Pages render inside <main>.

import { esc } from './api';
import { HUB_NAME } from './brand';
import { quickLink, type Tool } from './pages/tools';

export interface ShellState {
  name: string;
  isTechnology: boolean;
  notice: string;
  // The routed page, from routeFor in search.ts.
  page: string;
  // The search box's text, on the search page.
  query?: string;
  // The quick-access tools, on every signed-in page.
  tools?: Tool[];
}

// Search sits in the menu bar, outside the nav landmark, and submits through
// the page's existing search action.
function searchForm(query: string): string {
  return `<form class="navsearch" data-action="search" role="search">
      <label for="nav-q" class="visually-hidden">Search the Hub</label>
      <input id="nav-q" name="q" type="search" value="${esc(query)}" placeholder="Search the Hub" autocomplete="off" />
      <button type="submit">Search</button>
    </form>`;
}

function quickAccess(tools: Tool[]): string {
  if (!tools.length) return '';
  return `<aside class="quick" aria-label="Quick access">
    <h2 class="quick-title">Quick access</h2>
    <ul>${tools.map((t) => `<li>${quickLink(t)}</li>`).join('')}</ul>
  </aside>`;
}

// Support is not a page: its link opens Para Agent's chat (agent-dock.ts),
// so it is never the current section.
const NAV: Array<{ href: string; label: string; pages: string[]; technology?: boolean; dialog?: boolean }> = [
  { href: '#/', label: 'Home', pages: ['home', 'search', 'system'] },
  { href: '#/calendar', label: 'Change calendar', pages: ['calendar', 'entry'] },
  { href: '#/support', label: 'Support', pages: [], dialog: true },
  { href: '#/guides', label: 'Guides', pages: ['guides'] },
  { href: '#/tools', label: 'Tools', pages: ['tools', 'teleos'] },
  { href: '#/publish', label: 'Publish', pages: ['publish'], technology: true },
];

// The official outlined monogram, cropped off the right edge as framing.
const MONOGRAM = '<img class="monogram" src="/monogram.webp" alt="" aria-hidden="true" />';
const LOGO = '<img class="logo" src="/logo-reverse.png" alt="Paramount Liquor" width="168" height="44" />';

function footer(): string {
  return `<footer class="foot">
  <div class="inner">
    <span class="foot-tag">${esc(HUB_NAME)}</span>
    <p>Proof of concept for Paramount Liquor staff. Sample content is marked as sample.</p>
  </div>
</footer>`;
}

export function renderLayout(state: ShellState, body: string): string {
  const links = NAV.filter((n) => !n.technology || state.isTechnology)
    .map((n) => {
      const current = n.pages.includes(state.page) ? ' aria-current="page"' : '';
      const dialog = n.dialog ? ' aria-haspopup="dialog"' : '';
      return `<a href="${n.href}"${current}${dialog}>${esc(n.label)}</a>`;
    })
    .join('');
  return `<a class="skip" href="#main" data-skip>Skip to content</a>
<header class="masthead">
  ${MONOGRAM}
  <div class="inner">
    <a class="home-link" href="#/">${LOGO}</a>
    <div class="titles">
      <div class="title">${esc(HUB_NAME)}</div>
    </div>
    <div class="side">
      <span class="chip">Proof of concept</span>
      <span class="who"><strong>${esc(state.name)}</strong> <button type="button" class="ghost" data-action="sign-out">Sign out</button></span>
    </div>
  </div>
  <div class="mainnav">
    <div class="inner">
      <nav aria-label="Hub">${links}</nav>
      ${searchForm(state.query ?? '')}
    </div>
  </div>
</header>
<div class="page">
  <p class="banner" role="note">${esc(state.notice)}</p>
  <div class="page-body">
    ${quickAccess(state.tools ?? [])}
    <main id="main" tabindex="-1">${body}</main>
  </div>
</div>
${footer()}`;
}

// The landing screen before sign-in, like the uploader's landing state: a
// tall masthead with the big title, the notice overlapping its bottom edge,
// and one panel with the sign-in button. The message says what automatic
// sign-in is doing, or that it could not finish.
export function renderSignedOut(notice: string, message = ''): string {
  const status = message ? `\n    <p class="status" role="status">${esc(message)}</p>` : '';
  return `<header class="masthead landing">
  ${MONOGRAM}
  <div class="inner">
    ${LOGO}
    <div class="titles">
      <h1 class="title">${esc(HUB_NAME)}</h1>
      <p class="lede">Find what is changing, get support, and learn the tools you use. One place, one sign-in.</p>
    </div>
  </div>
</header>
<div class="page landing-page">
  <p class="banner" role="note">${esc(notice)}</p>
  <section class="panel signin">
    <h2><span class="n">1</span>Sign in to continue</h2>
    <p>Use your Paramount Liquor Microsoft account. You only sign in once: Support and Para Agent use the same sign-in.</p>${status}
    <button type="button" id="sign-in" class="primary">Sign in with Microsoft</button>
  </section>
</div>
${footer()}`;
}
